<template>
  <v-form
    ref="tracker-form"
    class="tracker-wrapper-form"
    @submit.prevent="submitEventHandler"
  >
    <div class="d-flex align-center flex-wrap ga-3">
      <v-btn
        variant="text"
        prepend-icon="mdi-arrow-left"
        class="px-2"
        @click="$emit('change:view')"
      >
        Back to list
      </v-btn>
      <v-spacer />
      <!-- what the request is decides the field labels below; the icons and colours match the request list -->
      <v-btn-toggle
        v-model="tempRecord.type"
        mandatory
        divided
        variant="outlined"
        density="comfortable"
        :color="tempRecord.type === 'feature' ? 'info' : 'error'"
      >
        <v-btn value="feature">
          <v-icon start color="info">mdi-lightbulb-on-outline</v-icon>
          New feature
        </v-btn>
        <v-btn value="bug">
          <v-icon start color="error">mdi-bug-outline</v-icon>
          Bug
        </v-btn>
      </v-btn-toggle>
    </div>

    <div class="tracker-wrapper-form__fields">
      <v-text-field
        v-model="tempRecord.name"
        :rules="[(v) => !!v || 'Required']"
        :label="labelName"
      />
      <v-textarea
        v-model="tempRecord.description"
        :rules="[(v) => !!v || 'Required']"
        label="Details"
        :hint="labelDescription"
        persistent-hint
        rows="5"
        auto-grow
      />

      <Upload
        v-model="uploadScreenshotDialog"
        :options="uploadOptions"
        source-type="bug-tracker"
        @fileUploaded="uploadSuccess"
      >
        <!-- a quiet drop area rather than a second primary action next to Send -->
        <v-card
          variant="outlined"
          class="tracker-wrapper-form__attach"
          @click="uploadScreenshotDialog = true"
        >
          <v-card-text class="d-flex align-center ga-3">
            <v-icon color="medium-emphasis">mdi-paperclip</v-icon>
            <div>
              <div class="text-body-2">Attach screenshots or PDFs</div>
              <div class="text-caption text-medium-emphasis">A screenshot of the problem helps us fix it faster</div>
            </div>
          </v-card-text>
        </v-card>
      </Upload>

      <v-row v-if="tempRecord.screenshots.length" dense>
        <v-col
          v-for="screenshot in tempRecord.screenshots"
          :key="screenshot.url"
          cols="6"
          sm="4"
          md="3"
        >
          <v-card class="attachment-card" variant="outlined">
            <embed
              v-if="screenshot.mimetype === fileMimetypesEnum.pdf"
              :src="getPDFPreviewUrl(screenshot.url)"
              width="100%"
              height="120"
              type="application/pdf"
            >
            <v-img
              v-else
              :src="screenshot.url"
              height="120"
              class="cursor-pointer"
              cover
              @click="previewImage(screenshot.url)"
            />
            <v-btn
              size="x-small"
              icon
              variant="flat"
              class="attachment-card__close-btn bg-surface"
              aria-label="Remove attachment"
              @click="removeAttachment(screenshot)"
            >
              <v-icon size="small">mdi-close</v-icon>
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-divider class="mt-6" />
    <div class="d-flex justify-end ga-2 py-4">
      <v-btn variant="text" @click="$emit('change:view')">Cancel</v-btn>
      <v-btn color="primary" :loading="saveLoading" type="submit">Send request</v-btn>
    </div>
    <PhotoViewer v-model="previewDialog" :image-url="imageUrlToPreview" />
  </v-form>
</template>

<script>
import { FileMimetypesEnum } from '@/bundles/Upload/enums/fileMimetypes';
import { getPDFPreviewUrl } from '@/bundles/Upload/helpers';

import Upload from '@/bundles/Upload/components/Upload';
import PhotoViewer from '@/bundles/App/components/dialogs/PhotoViewer.vue';

export default {
  name: 'TrackerWrapperForm',

  components: {
    PhotoViewer,
    Upload,
  },

  props: {
    saveLoading: Boolean
  },

  emits: ['change:view', 'send'],

  data: () => ({
    tempRecord: {
      type: 'feature',
      name: '',
      screenshots: [],
      description: '',
      url: ''
    },
    uploadScreenshotDialog: false,
    uploadOptions: {
      accept: `${FileMimetypesEnum.image}, ${FileMimetypesEnum.pdf}`,
      multiple: true,
    },
    fileMimetypesEnum: FileMimetypesEnum,
    getPDFPreviewUrl,
    imageUrlToPreview: null,
    previewDialog: false,
  }),

  computed: {
    labelName () {
      switch (this.tempRecord.type) {
        case 'feature':
          return 'What would you like to see?'
        default:
          return 'What went wrong?'
      }
    },
    labelDescription () {
      switch (this.tempRecord.type) {
        case 'feature':
          return 'How would this feature help you and your business?'
        default:
          return 'What did you do, what did you expect, and what happened instead?'
      }
    },
  },

  watch: {
    saveLoading (value) {
      if (!value) {
        this.resetForm();
      }
    },
    previewDialog (value) {
      if (!value) {
        this.imageUrlToPreview = null;
      }
    }
  },

  created () {
    this.$eventBus.$on('fill-bug-report', this.fillBugReport)
  },

  beforeUnmount () {
    this.$eventBus.$off('fill-bug-report', this.fillBugReport)
  },

  methods: {
    /**
     * Handler for upload success event
     * @param {Object} result
     * */
    uploadSuccess: function ({ files }) {
      this.tempRecord.screenshots.push(...files);
      this.uploadScreenshotDialog = false;
    },
    /**
     * Handler for submit form event
     * */
    submitEventHandler: async function () {
      const $form = this.$refs['tracker-form']
      const { valid } = await $form.validate()
      if (valid) {
        const payload = Object.assign({}, this.tempRecord, {
          screenshot: this.tempRecord.screenshots,
          description: `${this.tempRecord.description}${this.tempRecord.url ? `; URL: ${this.tempRecord.url}` : ''}`
        })
        this.$emit('send', payload)
      }
    },
    /**
     * Reset form method
     * */
    resetForm: function () {
      const $form = this.$refs['tracker-form']
      this.tempRecord.screenshots = [];
      this.tempRecord.url = ''
      $form.reset()
      $form.resetValidation()
    },
    fillBugReport: function ({ message, url, name = 'Report from notification' }) {
      this.tempRecord.type = 'bug';
      this.tempRecord.name = name
      this.tempRecord.description = message;
      this.tempRecord.url = url;
    },
    removeAttachment: function (screenshot) {
      this.tempRecord.screenshots = this.tempRecord.screenshots.filter(item => item.url !== screenshot.url);
    },
    previewImage: function (url) {
      this.imageUrlToPreview = url;
      this.previewDialog = true;
    },
  },
}
</script>

<style scoped>
.tracker-wrapper-form__fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
}

.tracker-wrapper-form__attach {
  border-style: dashed;
  border-color: rgba(var(--v-theme-on-surface), 0.3);
}
</style>
